import {
  useCallback,
  useEffect,
  useRef,
  useState,
  startTransition,
} from "react";
import { useNavigationGuardStore } from "@/store/navigationGuardStore";
import type { SaveBarPhase } from "@/components/common/SaveBar";

const SUCCESS_HIDE_MS = 1500;

export function useUnsavedFormGuard(hasUnsavedChanges: boolean) {
  const [saveBarPhase, setSaveBarPhase] = useState<SaveBarPhase | "hidden">(
    "hidden",
  );
  const saveBarSuccessTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const setBlocking = useNavigationGuardStore((s) => s.setBlocking);

  const clearSaveBarHideTimeout = useCallback(() => {
    if (saveBarSuccessTimeoutRef.current) {
      clearTimeout(saveBarSuccessTimeoutRef.current);
      saveBarSuccessTimeoutRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    setBlocking(hasUnsavedChanges);
    return () => {
      setBlocking(false);
    };
  }, [hasUnsavedChanges, setBlocking]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      clearSaveBarHideTimeout();
      startTransition(() => {
        setSaveBarPhase("unsaved");
      });
      return;
    }

    startTransition(() => {
      setSaveBarPhase((current) =>
        current === "unsaved" ? "hidden" : current,
      );
    });
  }, [hasUnsavedChanges, clearSaveBarHideTimeout]);

  useEffect(() => () => clearSaveBarHideTimeout(), [clearSaveBarHideTimeout]);

  const notifySaved = useCallback(() => {
    clearSaveBarHideTimeout();
    setSaveBarPhase("success");
    saveBarSuccessTimeoutRef.current = setTimeout(() => {
      setSaveBarPhase((current) => (current === "success" ? "hidden" : current));
    }, SUCCESS_HIDE_MS);
  }, [clearSaveBarHideTimeout]);

  return {
    saveBarPhase,
    notifySaved,
    clearSaveBarHideTimeout,
  };
}
